"use client";

import React, { createContext, useState, useEffect, useContext } from "react";
import Cookies from "js-cookie";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "@/config/firebaseConfig.js";

export const UserContext = createContext();

export const useAuth = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  // ----------------------
  // Cookie consent toggles
  // ----------------------
  const [isEssentialChecked, setIsEssentialChecked] = useState(
    Cookies.get("essential") ? true : true
  );
  const [isAnalyticalChecked, setIsAnalyticalChecked] = useState(
    Cookies.get("analytical") ? true : false
  );
  const [isMarketingChecked, setIsMarketingChecked] = useState(
    Cookies.get("marketing") ? true : false
  );

  const [isCookiePopupOpen, setIsCookiePopupOpen] = useState(false);

  // ---------------------------
  // ‼️  3rd‑party integrations ‼️
  // ---------------------------

  // GTM (loads GTM only once per session)
  const initializeGoogleTagManager = () => {
    if (typeof window !== "undefined" && !window.gtmInitialized) {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtm.js?id=GTM-KLBS7JZ2";
      document.head.appendChild(script);

      const noscript = document.createElement("noscript");
      noscript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KLBS7JZ2"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.appendChild(noscript);

      window.gtmInitialized = true;
    }
  };

  // GA4
  const initializeGoogleAnalytics = () => {
    if (typeof window !== "undefined" && !window.gtag) {
      const script = document.createElement("script");
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-EZ3S7MZ4PD";
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;

        gtag("js", new Date());
        gtag("config", "G-EZ3S7MZ4PD", { anonymize_ip: true });
      };
    } else if (typeof window !== "undefined") {
      window.gtag("config", "G-EZ3S7MZ4PD", { anonymize_ip: true });
    }
  };

  // Hotjar
  const initializeHotjar = () => {
    if (typeof window !== "undefined" && !window.hj) {
      (function (h, o, t, j, a, r) {
        h.hj =
          h.hj ||
          function () {
            (h.hj.q = h.hj.q || []).push(arguments);
          };
        h._hjSettings = { hjid: 5252923, hjsv: 6 };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
    }
  };

  // Microsoft Clarity 👇 (NEW)
  const initializeClarity = () => {
    if (typeof window !== "undefined" && !window.clarityLoaded) {
      (function (c, l, a, r, i, t, y) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", "r785c53l18");

      window.clarityLoaded = true;
    }
  };

  // TikTok Pixel
  const initializeTikTokPixel = () => {
    if (typeof window !== "undefined" && !window.ttqLoaded) {
      const script = document.createElement("script");
      script.innerHTML = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){
            var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;
            ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,
            ttq._t=ttq._t||{},ttq._t[e]=+new Date,
            ttq._o=ttq._o||{},ttq._o[e]=n||{};
            n=document.createElement("script");
            n.type="text/javascript",n.async=!0,
            n.src=r+"?sdkid="+e+"&lib="+t;
            e=document.getElementsByTagName("script")[0];
            e.parentNode.insertBefore(n,e)
          };
          ttq.load('CVMHDHRC77U34F1TL7U0');
          ttq.page();
        }(window, document, 'ttq');
      `;
      document.head.appendChild(script);
      window.ttqLoaded = true;
    }
  };

  // Meta / Facebook Pixel
  const initializeMetaPixel = () => {
    if (typeof window !== "undefined") {
      const script = document.createElement("script");
      script.innerHTML = `
        !function(f,b,e,v,n,t,s) {
          if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1146667273679466');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);

      const noscript = document.createElement("noscript");
      noscript.innerHTML = `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=1146667273679466&ev=PageView&noscript=1" />
      `;
      document.body.appendChild(noscript);
    }
  };

  // ------------------------
  //  Load scripts conditionally
  // ------------------------

  // Essential → nothing to do, they are always allowed in your app.

  // Analytical cookies
  useEffect(() => {
    if (isAnalyticalChecked) {
      // Enable GA
      window["ga-disable-G-EZ3S7MZ4PD"] = false;

      initializeGoogleAnalytics();
      initializeGoogleTagManager();
      initializeHotjar();
      initializeClarity();
      initializeTikTokPixel();
    } else {
      // Explicitly disable GA if the user revoked consent later
      if (typeof window !== "undefined") {
        window["ga-disable-G-EZ3S7MZ4PD"] = true;
      }
    }
  }, [isAnalyticalChecked]);

  // Marketing cookies
  useEffect(() => {
    if (isMarketingChecked) {
      initializeMetaPixel();
    }
  }, [isMarketingChecked]);

  // ------------------
  //  UI / UX helpers
  // ------------------
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Prevent horizontal scrolling
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowX = "hidden";
      document.documentElement.style.width = "100%";
      document.body.style.width = "100%";
    }
  }, []);

  useEffect(() => {
    const consent =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("cookieConsentAccepted");
    setIsCookiePopupOpen(consent !== "true");
  }, []);

  // -------------------------
  //  Authentication listeners
  // -------------------------
  const [userData, setUserData] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  // Clean up URL after Firebase redirect
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.has("apiKey") || params.has("oobCode") || params.has("mode")) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (!currentUser) {
        setUserData(null);
        setLoggedIn(false);
        setLoading(false);
        return;
      }

      const userRef = doc(db, "Users", currentUser.email);

      const unsubscribeSnapshot = onSnapshot(
        userRef,
        async (snapshot) => {
          if (!snapshot.exists()) {
            console.warn("No user data found in Firestore.");
            setUserData(null);
            setLoggedIn(false);
            setLoading(false);
            return;
          }

          const user = snapshot.data();

          if (user.isAdmin) {
            console.warn("🚨 Admin detected! Logging out...");
            await auth.signOut();
            setUserData(null);
            setLoggedIn(false);
            setLoading(false);
            return;
          }

          setUserData(user);
          setLoggedIn(true);
          setLoading(false);
        },
        (error) => {
          console.error("Firestore snapshot listener error:", error);
          setUserData(null);
          setLoggedIn(false);
          setLoading(false);
        }
      );

      return () => unsubscribeSnapshot();
    });

    return () => unsubscribeAuth();
  }, []);

  // ----------------
  //  Render provider
  // ----------------
  return (
    <UserContext.Provider
      value={{
        isEssentialChecked,
        setIsEssentialChecked,
        isAnalyticalChecked,
        setIsAnalyticalChecked,
        isMarketingChecked,
        setIsMarketingChecked,
        isCookiePopupOpen,
        setIsCookiePopupOpen,
        initializeGoogleAnalytics, // left exposed for manual calls if needed
        userData,
        loading,
        loggedIn,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
